/**
 * ModelPicker — thin adapter over the shared `@flowstate/ai-client` ModelPicker.
 *
 * Keeps the app-local props contract (selectedProvider/onProviderChange +
 * selectedModel/onModelChange) so existing composer mount points don't change
 * shape, and adds optional reasoning-effort state. The shared component picks
 * provider per catalog row (no provider segmented control), so the old
 * per-provider default-model reset on switch is no longer needed.
 */
import { ModelPicker as SharedModelPicker, useModelCatalog } from "@flowstate/ai-client";
import type { ReasoningEffort } from "@flowstate/types";
import { supabase } from "@/integrations/supabase/client";

type Provider = "gemini" | "openrouter";

interface ModelPickerProps {
  selectedProvider: Provider;
  onProviderChange: (provider: Provider) => void;
  selectedModel: string;
  onModelChange: (model: string) => void;
  reasoningEffort?: ReasoningEffort;
  onReasoningEffortChange?: (effort: ReasoningEffort) => void;
  isGeminiDelegated?: boolean;
  disabled?: boolean;
  variant?: "pill" | "icon";
}

async function getAccessToken(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return null;
  }
}

export function ModelPicker({
  selectedProvider,
  onProviderChange,
  selectedModel,
  onModelChange,
  reasoningEffort,
  onReasoningEffortChange,
  isGeminiDelegated = false,
  disabled = false,
  variant = "pill",
}: ModelPickerProps) {
  const { data: catalog } = useModelCatalog({ getAccessToken });

  return (
    <SharedModelPicker
      variant={variant}
      disabled={disabled}
      isGeminiDelegated={isGeminiDelegated}
      catalog={catalog}
      value={{ provider: selectedProvider, model: selectedModel, effort: reasoningEffort ?? "medium" }}
      onChange={(next) => {
        if (next.provider !== selectedProvider) onProviderChange(next.provider);
        if (next.model !== selectedModel) onModelChange(next.model);
        onReasoningEffortChange?.(next.effort);
      }}
    />
  );
}

export default ModelPicker;
